import { configTabs } from '../pages/admin/configTabs'
import { AUDIT_TABS } from '../pages/admin/auditTabs'
import { AUTOMATION_TABS } from '../pages/admin/automationTab'
import { inventoryViewAccess, inventoryViewLabelKey, inventoryViewSearch, visibleInventoryViews } from '../pages/inventory/inventoryTab'
import { ORG_TABS } from './organizationTabs'
import { activityFundTabSearch } from '../pages/operations/activityFundTab'
import { requestsTabHref } from '../pages/operations/requestsTab'
import { payrollTabs } from '../pages/payroll/payrollTabs'
import { LEAVE_ADMIN_ABILITY, TIME_ADMIN_ABILITIES, offeredScopes } from '../pages/time/scopes'
import { canAny, can, type User } from './types'

export type NavTab = { to: string; labelKey: string }

const tabHref = (path: string, tab: string) => `${path}?tab=${tab}`

function scopeTabs(path: string, isAdmin: boolean): NavTab[] {
  return offeredScopes(isAdmin).map((scope) => ({
    to: scope === 'own' ? path : `${path}?scope=${scope}`,
    labelKey: `time.scope_${scope}`,
  }))
}

const CONFIG_LABEL: Record<string, string> = {
  activities: 'timeadmin.activities',
  products: 'timeadmin.products',
  calendar: 'app.adminCalendar',
  org: 'org.tabOrganization',
  otp: 'org.tabSignIn',
}

const PAYROLL_LABEL: Record<string, string> = {
  payroll: 'app.payroll',
  oneoff: 'oneOff.tab',
  rates: 'payroll.rates.tab',
  access: 'app.adminAuditLog',
}

const AUDIT_LABEL: Record<string, string> = { changes: 'app.adminChanges', person: 'app.adminPersonTrail' }

const ORG_LABEL: Record<string, string> = { orgchart: 'hr.orgTabChart', directory: 'hr.orgTabDirectory' }

const AUTOMATION_LABEL: Record<string, string> = {
  rules: 'automation.tabRules',
  messages: 'automation.tabMessages',
}

/** The inner tabs of a single-page app as this viewer sees them, driven by each page's own gate; empty when the app has none. */
export function navTabsFor(appKey: string, prefix: string, user: User | undefined): NavTab[] {
  switch (appKey) {
    case 'activityexpense':
      return [
        { to: prefix, labelKey: 'activityFund.expensesTitle' },
        { to: `${prefix}${activityFundTabSearch('', 'contributions')}`, labelKey: 'activityFund.contributionsTitle' },
        { to: `${prefix}${activityFundTabSearch('', 'extra')}`, labelKey: 'activityFund.extraIncomeTitle' },
      ]
    case 'requests':
      return [
        { to: requestsTabHref('requests'), labelKey: 'app.requests' },
        { to: requestsTabHref('trips'), labelKey: 'app.trips' },
      ]
    case 'time':
      return scopeTabs(prefix, canAny(user, TIME_ADMIN_ABILITIES))
    case 'leave':
      return scopeTabs(prefix, can(user, LEAVE_ADMIN_ABILITY))
    case 'payroll':
      return payrollTabs((ability) => can(user, ability)).map((tab) => ({
        to: tabHref(prefix, tab),
        labelKey: PAYROLL_LABEL[tab],
      }))
    case 'adminaudit':
      return AUDIT_TABS.map((tab) => ({ to: tabHref(prefix, tab), labelKey: AUDIT_LABEL[tab] }))
    case 'adminconfig':
      return configTabs(user).map((tab) => ({ to: tabHref(prefix, tab), labelKey: CONFIG_LABEL[tab] }))
    case 'organization':
      return ORG_TABS.map((tab) => ({ to: tabHref(prefix, tab), labelKey: ORG_LABEL[tab] }))
    case 'inventory':
      return visibleInventoryViews(inventoryViewAccess(user)).map((view) => ({
        to: `${prefix}${inventoryViewSearch('', view)}`,
        labelKey: inventoryViewLabelKey(view),
      }))
    case 'adminautomation':
      return AUTOMATION_TABS.map((tab) => ({ to: tabHref(prefix, tab), labelKey: AUTOMATION_LABEL[tab] }))
    default:
      return []
  }
}
